import Router from 'next/router';
import { useCallback } from 'react';

import { useTrackAppliedSearchTerm, useTrackRemovedSearchTerm } from '@air/analytics';

import { QueryParamNames } from '~/constants/search';
import { useFiltersContext } from '~/providers/FiltersProvider';
import { usePrivateSearchContext } from '~/providers/PrivateSearchProvider';
import { getPathWithOmittedParams, getPathWithoutQuery, pushWithExistingQuery } from '~/utils/PathUtils';

export type UseSearchParamsReturn = {
  search: string | undefined;
  hasSearchParams: boolean;
  clearSearch: () => void;
  setSearch: (term: string) => Promise<boolean> | undefined;
};

export const useSearchParams = (): UseSearchParamsReturn => {
  const { search } = useFiltersContext();
  const { searchType } = usePrivateSearchContext();

  const { trackAppliedSearchTerm } = useTrackAppliedSearchTerm();
  const { trackRemovedSearchTerm } = useTrackRemovedSearchTerm();

  const changeQueryParams = useCallback(
    (term: string) => {
      if (term === search) {
        return;
      }

      if (term) {
        trackAppliedSearchTerm({
          term,
          search_type: searchType === 'keyword' ? 'exact' : 'smart',
        });
      }

      return pushWithExistingQuery({
        path: getPathWithoutQuery(Router.asPath),
        newQuery: { [QueryParamNames.Search]: term },
      });
    },
    [search, trackAppliedSearchTerm, searchType],
  );

  const clearSearch = useCallback(() => {
    trackRemovedSearchTerm();
    return Router.push(getPathWithOmittedParams(Router.asPath, [QueryParamNames.Search]), undefined, { shallow: true });
  }, [trackRemovedSearchTerm]);

  return {
    search,
    clearSearch,
    setSearch: changeQueryParams,
    hasSearchParams: !!search,
  };
};
